import { Argument, Command } from 'commander'

type MaybePromise<T> = T | Promise<T>

export interface AddCommandOptions {
  yes?: boolean
  skipMigration?: boolean
  integration?: boolean
  cwd?: string
}

export interface AddFieldCommandOptions {
  cwd?: string
  skipMigration?: boolean
  acceptGenerated?: boolean
  yes?: boolean
  type?: string
  field?: string
  label?: string
  slot?: string
  step?: string
  before?: string
  after?: string
  required?: boolean
  multiple?: boolean
  creatable?: boolean
  option?: string[]
  relationship?: string
}

export const CREATE_SCHEMA_KINDS = ['entity', 'single', 'form'] as const
export type CreateSchemaKind = (typeof CREATE_SCHEMA_KINDS)[number]

export interface CreateCommandOptions {
  cwd?: string
  skipMigration?: boolean
  acceptGenerated?: boolean
}

export interface GenerateCommandOptions {
  force: boolean
  acceptGenerated?: boolean
  all: boolean
  interactive: boolean
  skipMigration: boolean
  cwd?: string
}

export interface InitCommandOptions {
  presets?: string
  integrations?: string
  namespace?: string
  yes?: boolean
  skipMigration?: boolean
  skipAdminCreation?: boolean
  skipDevServerStart?: boolean
  databaseUrl?: string
  databaseProvider?: 'vercel' | 'railway' | 'manual'
  storageProvider?: 'vercel-blob' | 'railway-bucket' | 'r2' | 'local'
  deployProvider?: 'vercel' | 'railway' | 'none'
  /** Neon billing plan id forwarded to `vercel integration add neon`. */
  databasePlan?: string
  railwayWorkspace?: string
  railwayProject?: string
  railwayEnvironment?: string
  railwayService?: string
  railwayBucketRegion?: string
  force?: boolean
}

export interface ListPresetsCommandOptions {
  cwd?: string
}

export interface ListIntegrationsCommandOptions {
  cwd?: string
}

export interface RemoveCommandOptions {
  force?: boolean
  integration?: boolean
  cwd?: string
}

export interface RemoveSchemaCommandOptions {
  force: boolean
  cwd?: string
}

export interface SeedCommandOptions {
  cwd?: string
}

export interface UninstallCommandOptions {
  force: boolean
  cwd?: string
}

export interface UpdateCommandOptions {
  list?: boolean
  all?: boolean
  shadcnPreset?: string
  only?: string
  cwd?: string
}

export interface UpdateDepsCommandOptions {
  cwd?: string
}

export interface UpdateStylesCommandOptions {
  cwd?: string
}

export interface BetterstartCommandRuntime {
  runAdd(presets: string[], options: AddCommandOptions): MaybePromise<void>
  runAddField(schemaName: string | undefined, options: AddFieldCommandOptions): MaybePromise<void>
  runCreate(
    kind: CreateSchemaKind,
    schemaName: string | undefined,
    options: CreateCommandOptions
  ): MaybePromise<void>
  runGenerate(schemaName: string | undefined, options: GenerateCommandOptions): MaybePromise<void>
  runInit(name: string | undefined, options: InitCommandOptions): MaybePromise<void>
  runListIntegrations(options: ListIntegrationsCommandOptions): MaybePromise<void>
  runListPresets(options: ListPresetsCommandOptions): MaybePromise<void>
  runRemove(presets: string[], options: RemoveCommandOptions): MaybePromise<void>
  runRemoveSchema(schemaName: string, options: RemoveSchemaCommandOptions): MaybePromise<void>
  runSeed(options: SeedCommandOptions): MaybePromise<void>
  runUninstall(options: UninstallCommandOptions): MaybePromise<void>
  runUpdate(components: string[], options: UpdateCommandOptions): MaybePromise<void>
  runUpdateDeps(options: UpdateDepsCommandOptions): MaybePromise<void>
  runUpdateStyles(options: UpdateStylesCommandOptions): MaybePromise<void>
}

export function createAddCommand(runtime: BetterstartCommandRuntime): Command {
  return new Command('add')
    .description('Install BetterStart presets or integrations into an existing Admin')
    .argument('<items...>', 'Preset or integration IDs to install (e.g. blog, r2)')
    .option('-y, --yes', 'Skip interactive configuration prompts', false)
    .option('--integration', 'Install integration IDs instead of content preset IDs', false)
    .option(
      '--skip-migration',
      'Skip running db:push after installing schema-backed presets',
      false
    )
    .option('--cwd <path>', 'Project root path')
    .action((items: string[], options: AddCommandOptions) => runtime.runAdd(items, options))
}

function collectRepeatedOption(value: string, previous: string[] = []): string[] {
  return [...previous, value]
}

export function createAddFieldCommand(runtime: BetterstartCommandRuntime): Command {
  return new Command('add-field')
    .description('Add one field to an existing Generated schema')
    .argument('[schema]', 'Generated schema name (e.g. posts, settings, contact)')
    .option('--cwd <path>', 'Project root path')
    .option('--skip-migration', 'Skip running db:push after generation', false)
    .option(
      '--accept-generated',
      'Accept generated output for files that would otherwise get merge conflicts',
      false
    )
    .option('-y, --yes', 'Confirm warning cases in non-interactive mode', false)
    .option('--type <type>', 'Field type')
    .option('--field <name>', 'Field identifier')
    .option('--label <label>', 'Field label')
    .option('--slot <main|sidebar>', 'Entity or Single layout slot')
    .option('--step <name>', 'Form step target for multi-step form schemas')
    .option('--before <field>', 'Insert before an existing field in the selected target')
    .option('--after <field>', 'Insert after an existing field in the selected target')
    .option('--required', 'Mark the field as required', false)
    .option('--multiple', 'Enable multiple values for supported field types', false)
    .option('--creatable', 'Enable creatable options for Entity or Single select fields', false)
    .option('--option <value:Label>', 'Static select option', collectRepeatedOption, [])
    .option('--relationship <schema>', 'Relationship target schema')
    .action((schemaName: string | undefined, options: AddFieldCommandOptions) =>
      runtime.runAddField(schemaName, options)
    )
}

export function createCreateCommand(runtime: BetterstartCommandRuntime): Command {
  return new Command('create')
    .description('Interactively create and generate a user-authored schema')
    .addArgument(
      new Argument('<kind>', 'Schema kind: entity, single, or form').choices(CREATE_SCHEMA_KINDS)
    )
    .argument('[name]', 'Optional schema name override (derived from label when omitted)')
    .option('--cwd <path>', 'Project root path')
    .option('--skip-migration', 'Skip running db:push after generation', false)
    .option(
      '--accept-generated',
      'Accept generated output for files that would otherwise get merge conflicts',
      false
    )
    .action(
      (kind: CreateSchemaKind, schemaName: string | undefined, options: CreateCommandOptions) =>
        runtime.runCreate(kind, schemaName, options)
    )
}

export function createGenerateCommand(runtime: BetterstartCommandRuntime): Command {
  return new Command('generate')
    .alias('g')
    .description('Generate entity or form from a JSON schema')
    .argument('[schema]', 'Schema name (e.g. posts, categories, contact)')
    .option('-f, --force', 'Overwrite existing generated files', false)
    .option(
      '--accept-generated',
      'Accept generated output for files that would otherwise get merge conflicts',
      false
    )
    .option('-a, --all', 'Regenerate all schemas', false)
    .option('--interactive', 'Prompt during generate --all', false)
    .option('--skip-migration', 'Skip running db:push after generation', false)
    .option('--cwd <path>', 'Project root path')
    .action((schemaName: string | undefined, options: GenerateCommandOptions) =>
      runtime.runGenerate(schemaName, options)
    )
}

export function createInitCommand(runtime: BetterstartCommandRuntime): Command {
  return new Command('init')
    .description('Scaffold Admin into a new or existing Next.js app')
    .argument('[name]', 'Project name (creates new directory if fresh project)')
    .option('--presets <presets>', 'Comma-separated content preset IDs (e.g. blog)')
    .option('--integrations <integrations>', 'Comma-separated integration IDs (e.g. r2,resend)')
    .option('--namespace <name>', 'Generated admin namespace (e.g. admin, dashboard)')
    .option('-y, --yes', 'Skip prompts (defaults to manual database, local storage, and no deploy)')
    .option('--skip-migration', 'Skip running drizzle-kit push after scaffolding')
    .option('--skip-admin-creation', 'Skip prompting to create the initial admin user')
    .option('--skip-dev-server-start', 'Skip prompting to start the development server')
    .option(
      '--database-url <url>',
      'PostgreSQL database connection string (postgres:// or postgresql://)'
    )
    .option('--database-provider <provider>', 'Database provider: vercel, railway, or manual')
    .option(
      '--storage-provider <provider>',
      'Storage provider: vercel-blob, railway-bucket, r2, or local'
    )
    .option('--deploy-provider <provider>', 'Deploy provider: vercel, railway, or none')
    .option('--database-plan <id>', 'Neon plan id for a Vercel-provisioned database')
    .option('--railway-workspace <id-or-name>', 'Railway workspace for a new project')
    .option('--railway-project <id-or-name>', 'Existing Railway project to intentionally reuse')
    .option('--railway-environment <id-or-name>', 'Railway environment (default: production)')
    .option('--railway-service <id-or-name>', 'Railway app service (default: web)')
    .option(
      '--railway-bucket-region <region>',
      'Railway bucket region: sjc, iad, ams, or sin (default: iad)'
    )
    .option('--force', 'Overwrite all existing Admin files (nuclear option)')
    .action((name: string | undefined, options: InitCommandOptions) =>
      runtime.runInit(name, options)
    )
}

export function createListPresetsCommand(runtime: BetterstartCommandRuntime): Command {
  return new Command('list-presets')
    .description('List available BetterStart presets')
    .option('--cwd <path>', 'Project root path')
    .action((options: ListPresetsCommandOptions) => runtime.runListPresets(options))
}

export function createListIntegrationsCommand(runtime: BetterstartCommandRuntime): Command {
  return new Command('list-integrations')
    .description('List available BetterStart integrations')
    .option('--cwd <path>', 'Project root path')
    .action((options: ListIntegrationsCommandOptions) => runtime.runListIntegrations(options))
}

export function createRemoveCommand(runtime: BetterstartCommandRuntime): Command {
  return new Command('remove')
    .description('Remove installed BetterStart presets or integrations')
    .argument('<items...>', 'Preset or integration IDs to remove (e.g. blog r2)')
    .option('-f, --force', 'Skip confirmation prompt', false)
    .option('--integration', 'Remove integration IDs instead of content preset IDs', false)
    .option('--cwd <path>', 'Project root path')
    .action((items: string[], options: RemoveCommandOptions) => runtime.runRemove(items, options))
}

export function createRemoveSchemaCommand(runtime: BetterstartCommandRuntime): Command {
  return new Command('remove-schema')
    .description('Remove generated files for a user-authored schema')
    .argument('<schema>', 'Schema name to remove (e.g. posts, categories, contact)')
    .option('-f, --force', 'Skip confirmation prompt', false)
    .option('--cwd <path>', 'Project root path')
    .action((schemaName: string, options: RemoveSchemaCommandOptions) =>
      runtime.runRemoveSchema(schemaName, options)
    )
}

export function createSeedCommand(runtime: BetterstartCommandRuntime): Command {
  return new Command('seed')
    .description('Create the initial admin user')
    .option('--cwd <path>', 'Project root path')
    .action((options: SeedCommandOptions) => runtime.runSeed(options))
}

export function createUninstallCommand(runtime: BetterstartCommandRuntime): Command {
  return new Command('uninstall')
    .description('Remove all Admin files and undo modifications made by betterstart init')
    .option('-f, --force', 'Skip all confirmation prompts', false)
    .option('--cwd <path>', 'Project root path')
    .action((options: UninstallCommandOptions) => runtime.runUninstall(options))
}

export function createUpdateCommand(runtime: BetterstartCommandRuntime): Command {
  return new Command('update')
    .alias('update-component')
    .description('Update Admin components from the latest CLI templates')
    .argument('[components...]', 'Component names to update (e.g., media-field button)')
    .option('--list', 'List all available components')
    .option('--all', 'Update all components')
    .option('--shadcn-preset <preset>', 'Apply a shadcn preset to Admin UI components')
    .option('--only <parts>', 'Apply only supported shadcn preset parts: theme')
    .option('--cwd <path>', 'Project root path')
    .action((components: string[], options: UpdateCommandOptions) =>
      runtime.runUpdate(components, options)
    )
}

export function createUpdateDepsCommand(runtime: BetterstartCommandRuntime): Command {
  return new Command('update-deps')
    .description('Install or update all Admin dependencies')
    .option('--cwd <path>', 'Project root path')
    .action((options: UpdateDepsCommandOptions) => runtime.runUpdateDeps(options))
}

export function createUpdateStylesCommand(runtime: BetterstartCommandRuntime): Command {
  return new Command('update-styles')
    .description('Replace admin-globals.css with the latest version from the CLI')
    .option('--cwd <path>', 'Project root path')
    .action((options: UpdateStylesCommandOptions) => runtime.runUpdateStyles(options))
}
